// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState, getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useCuttingInstructions } from "@store/Framework/Store/Store/Data/CuttingInstructions/CuttingInstructionsHooks";
import { getCuttingInstructions } from "@store/Framework/Store/Store/Data/CuttingInstructions/CuttingInstructionsSelectors";
import setCuttingInstructions from "@store/Framework/Store/Store/Data/CuttingInstructions/Handlers/SetCuttingInstructions";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ChangeEvent, FocusEvent } from "react";
import { ResolveThunks } from "react-redux";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    let productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    let queryString = location.search;
    if (location.pathname.toLowerCase().startsWith("/content/page/") && productPath.indexOf("?") > -1) {
        queryString = productPath ? productPath.substring(productPath.indexOf("?")) : "";
        productPath = productPath.substring(0, productPath.indexOf("?"));
    }

    const productState = getProductStateByPath(state, productPath);
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const baseCuttingText = getCuttingInstructions(state);
    return {
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
    };
};

const mapDispatchToProps = {
    setCuttingInstructions,
};

const enum fields {
    checkboxLabel = "checkboxLabel",
    textFieldLabel = "textFieldLabel",
    helpText = "helpText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.checkboxLabel]: string;
        [fields.textFieldLabel]: string;
        [fields.helpText]: string;
    };
}

type Props = WidgetProps &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasProduct;

export interface CuttingInstructionsStyles {
    wrapper?: InjectableCss;
    addInstructionsCheckbox?: CheckboxProps;
    addInstructionsTextArea?: TextAreaProps;
}

export const cuttingInstructionsStyles: CuttingInstructionsStyles = {
    wrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
            border-radius: ${pxToRem(5)};
            padding: ${pxToRem(20)};
        `,
    },
    addInstructionsCheckbox: {
        mergeCss: true,
        css: css`
            font-weight: bold;
        `,
    },
};

const styles = cuttingInstructionsStyles;

const CuttingInstructions = ({
    fields: { checkboxLabel, textFieldLabel, helpText },
    isSignedIn,
    product,
    setCuttingInstructions,
}: Props) => {
    const [showTextField, setShowTextField] = React.useState(false);
    const [cuttingText, setCuttingText] = React.useState("");
    const cuttingInstructionsState = useCuttingInstructions();

    React.useEffect(() => {
        if (!cuttingInstructionsState.requiresCut) {
            setShowTextField(false);
            setCuttingText("");
        }
    }, [cuttingInstructionsState]);

    const cuttingInstructionsCheckboxChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setShowTextField(value);
        if (!value) {
            setCuttingInstructions({ productId: "", requiresCut: value, requiresCutDescription: "" });
        }
    };

    const cuttingInstructionsInputChangeHandler = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setCuttingText(event.currentTarget.value);
    };

    const cuttingInstructionsInputBlurHandler = (event: FocusEvent<HTMLTextAreaElement>) => {
        setCuttingInstructions({
            productId: product.id,
            requiresCut: showTextField,
            requiresCutDescription: event.target.value,
        });
    };

    if (
        !isSignedIn ||
        (product.properties["isCuttable"] && product.properties["isCuttable"] === "") ||
        (!product.properties["isCuttable"] &&
            !cuttingInstructionsState.isCuttableVariants.includes(product.productNumber))
    ) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <Checkbox
                {...styles.addInstructionsCheckbox}
                onChange={cuttingInstructionsCheckboxChangeHandler}
                checked={showTextField}
                uid="includeHeading"
                data-test-selector="cuttingInstructions_checkbox"
            >
                {checkboxLabel}
            </Checkbox>
            {showTextField && (
                <TextArea
                    label={`${textFieldLabel} *`}
                    value={cuttingText}
                    onChange={cuttingInstructionsInputChangeHandler}
                    onBlur={cuttingInstructionsInputBlurHandler}
                    hint={`* ${helpText}`}
                    {...styles.addInstructionsTextArea}
                    data-test-selector="cuttingInstructions_textArea"
                />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(CuttingInstructions)),
    definition: {
        group: "Product Details",
        icon: "rectangle-wide",
        displayName: "Add Cutting Instructions",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.checkboxLabel,
                displayName: "Checkbox Label",
                editorTemplate: "TextField",
                defaultValue: "Add cutting instructions",
                fieldType: "Contextual",
            },
            {
                name: fields.textFieldLabel,
                displayName: "Textfield Label",
                editorTemplate: "TextField",
                defaultValue: "Cutting instructions",
                fieldType: "Contextual",
            },
            {
                name: fields.helpText,
                displayName: "Hint for users",
                editorTemplate: "TextField",
                defaultValue:
                    "All custom cut products are non-refundable and may increase lead time. Additional fees will be added after checkout. Chat now for more details.",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
